import { LinearGradient } from 'expo-linear-gradient';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { useEffect } from 'react';
import Animated, { FadeInRight, FadeInUp } from 'react-native-reanimated';
import type { IconType } from 'smoothie';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Icon,
    isNonMobileWeb,
    LinkIcon,
    PiggyBankIcon,
    PillButton,
    ResponsiveScreenModal,
    ScrollView,
    ShieldCheckIcon,
    tokens,
    XStack,
    YStack,
} from 'smoothie';

import SafeImageBottom from '../../../assets/images/illustrations/secure-savings-bottom.png';
import SafeImageTop from '../../../assets/images/illustrations/secure-savings-top.png';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import {
    hasAcceptedInstitutionLinkDisclosuresAtom,
    isPartnerMembershipAtom,
    isShowingInstitutionLinkDisclosuresAtom,
} from '../state/HolisticAtoms';
import AboutInstitutionLinkingSheet from './AboutInstitutionLinkingSheet';

export const AboutInstitutionLinkingModal = () => {
    const [
        isShowingInstitutionLinkDisclosures,
        setIsShowingInstitutionLinkDisclosures,
    ] = useAtom(isShowingInstitutionLinkDisclosuresAtom);

    useEffect(() => {
        isShowingInstitutionLinkDisclosures &&
            LogRocketFacade.tagPage('AboutInstitutionLinking');
    }, [isShowingInstitutionLinkDisclosures]);

    const onOpenChange = (isOpen: boolean) => {
        !isOpen && setIsShowingInstitutionLinkDisclosures(false);
    };

    return (
        <ResponsiveScreenModal
            isOpen={isShowingInstitutionLinkDisclosures}
            onOpenChange={onOpenChange}
            title="Connecting to Fruitful"
            hasHeader={true}
            renderInAppRoot={isNonMobileWeb} // if native renders in app root, inner sheet can crash; if mobile web renders in app root, inner sheet is layered under the screen modal; if non-mobile web *doesn't* render in app root, the modal has a messed up size/layout
        >
            <AboutInstitutionLinkingContent
                onClose={() => {
                    setIsShowingInstitutionLinkDisclosures(false);
                }}
            />
        </ResponsiveScreenModal>
    );
};

const Highlight = ({
    icon,
    label,
    subLabel,
}: {
    icon: IconType;
    label: string;
    subLabel: string;
}) => {
    return (
        <XStack marginLeft={-10}>
            <Icon icon={icon} size={20} color="$gray1200" />
            <YStack paddingLeft={15} marginTop={-3} gap={5} width="95%">
                <BodyLargeEmphasized>{label}</BodyLargeEmphasized>
                <BodySmallRegular>{subLabel}</BodySmallRegular>
            </YStack>
        </XStack>
    );
};

const AboutInstitutionLinkingContent = ({
    onClose,
}: {
    onClose: () => void;
}) => {
    const isPartnerMembership = useAtomValue(isPartnerMembershipAtom);
    const setHasAcceptedInstitutionLinkDisclosures = useSetAtom(
        hasAcceptedInstitutionLinkDisclosuresAtom,
    );
    const [isSheetOpen, setIsSheetOpen] = React.useState(false);

    return (
        <>
            <YStack
                flex={1}
                height="100%"
                justifyContent="space-between"
                alignItems="center"
                maxWidth={500}
                alignSelf="center"
            >
                <YStack flexShrink={1}>
                    <ScrollView
                        paddingHorizontal={8}
                        showsVerticalScrollIndicator={false}
                    >
                        <YStack
                            height="100%"
                            paddingBottom={20}
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <YStack marginVertical={12}>
                                <Animated.Image
                                    key={0}
                                    source={SafeImageTop}
                                    entering={FadeInUp.duration(700).delay(300)}
                                    style={{ height: 60, width: 115 }}
                                />
                                <Animated.Image
                                    key={1}
                                    source={SafeImageBottom}
                                    entering={FadeInRight.duration(300).delay(
                                        150,
                                    )}
                                    style={{ height: 105, width: 120 }}
                                />
                            </YStack>
                            <YStack alignItems="center" marginTop={30}>
                                <BodyMediumRegular textAlign="center">
                                    Fruitful uses your account balances,
                                    details, and transactions to provide you
                                    with financial insights and help your Guide
                                    build your roadmap.
                                </BodyMediumRegular>
                                <YStack
                                    marginHorizontal={20}
                                    marginTop={30}
                                    marginBottom={20}
                                    gap={20}
                                    $md={{ paddingBottom: 20 }}
                                >
                                    <Highlight
                                        icon={PiggyBankIcon}
                                        label={`${isPartnerMembership ? 'Your joint' : 'Your'} financial home`}
                                        subLabel={`All your financial accounts in one place — a shared view for ${isPartnerMembership ? 'you, your partner, and your Guide' : 'you and your Guide'}.`}
                                    />
                                    <Highlight
                                        icon={LinkIcon}
                                        label="Sharing only what's important"
                                        subLabel={`Your Guide will have access to only the info they need to provide you ${isPartnerMembership ? 'and your partner with personalized advice' : 'with personalized advice'}.`}
                                    />
                                    <Highlight
                                        icon={ShieldCheckIcon}
                                        label="Secure and safe"
                                        subLabel="We never share or sell your data, which is protected by bank-level security and encryption."
                                    />
                                </YStack>
                                <PillButton
                                    text="Learn more"
                                    onPress={() => {
                                        setIsSheetOpen(true);
                                    }}
                                />
                            </YStack>
                        </YStack>
                    </ScrollView>
                    <LinearGradient
                        colors={[
                            'rgba(255, 255, 255, 0)',
                            tokens.color.light.brand.white,
                        ]}
                        style={{
                            height: 30,
                            width: '100%',
                            position: 'absolute',
                            bottom: 0,
                        }}
                        pointerEvents="none"
                    />
                </YStack>
                <XStack
                    paddingTop={5}
                    width="100%"
                    $sm={{
                        paddingTop: 24,
                    }}
                >
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        tone="positive"
                        size={ButtonSize.LARGE}
                        text="Continue"
                        onPress={() => {
                            setHasAcceptedInstitutionLinkDisclosures(true);
                            onClose();
                        }}
                    />
                </XStack>
            </YStack>

            <AboutInstitutionLinkingSheet
                isPartnerMembership={isPartnerMembership}
                isOpen={isSheetOpen}
                onClose={() => {
                    setIsSheetOpen(false);
                }}
            />
        </>
    );
};
